import { useState, useCallback } from "react";
import axios from "axios";

const TOPICS = [
  { key: "mix", emoji: "🎲", it: "Mix", en: "Mix" },
  { key: "animali", emoji: "🐾", it: "Animali", en: "Animals" },
  { key: "spazio", emoji: "🚀", it: "Spazio", en: "Space" },
  { key: "parole", emoji: "🔤", it: "Parole", en: "Words" },
  { key: "numeri", emoji: "🔢", it: "Numeri", en: "Numbers" },
  { key: "natura", emoji: "🌿", it: "Natura", en: "Nature" },
];

export default function AIQuizGame({ child, lang, api, t, onBack, onComplete }) {
  const [phase, setPhase] = useState("topic"); // topic, loading, playing, results, error
  const [topic, setTopic] = useState("mix");
  const [questions, setQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [current, setCurrent] = useState(0);
  const [feedback, setFeedback] = useState(null); // {selected, correctIndex, isCorrect}
  const [results, setResults] = useState(null);

  const startQuiz = useCallback(async (chosenTopic) => {
    setPhase("loading");
    setTopic(chosenTopic);
    setFeedback(null);
    setCurrent(0);
    setResults(null);
    try {
      const res = await axios.post(`${api}/ai-quiz/start?child_id=${child.id}&topic=${chosenTopic}`, null, { timeout: 60000 });
      setQuestions(res.data.questions);
      setSessionId(res.data.session_id);
      setPhase("playing");
    } catch (e) {
      console.error("AI quiz start error:", e);
      setPhase("error");
    }
  }, [api, child.id]);

  const answer = async (idx) => {
    if (feedback) return;
    try {
      const res = await axios.post(`${api}/ai-quiz/${sessionId}/answer`, {
        question_index: current,
        answer_index: idx,
        time_ms: 2000
      });
      setFeedback({ selected: idx, correctIndex: res.data.correct_index, isCorrect: res.data.is_correct });
      setTimeout(async () => {
        if (current < questions.length - 1) {
          setCurrent(current + 1);
          setFeedback(null);
        } else {
          const done = await axios.post(`${api}/ai-quiz/${sessionId}/complete`);
          setResults(done.data);
          setPhase("results");
          onComplete(done.data);
        }
      }, 1300);
    } catch (e) {
      console.error("AI quiz answer error:", e);
    }
  };

  const q = questions[current];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-700 via-fuchsia-600 to-pink-500 p-4 flex items-center justify-center">
      <div className="max-w-lg w-full">
        <button
          onClick={onBack}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-4"
          data-testid="ai-quiz-back-btn"
        >
          ← {t.backHome}
        </button>

        {phase === "topic" && (
          <div className="bg-white rounded-3xl p-8 shadow-2xl" data-testid="ai-quiz-topic-select">
            <div className="text-center mb-6">
              <div className="text-5xl mb-2">✨</div>
              <h2 className="text-2xl font-bold text-purple-600">{t.aiQuiz}</h2>
              <p className="text-gray-500">{lang === "it" ? "Scegli un tema per le tue sfide!" : "Choose a topic for your challenges!"}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {TOPICS.map((tp) => (
                <button
                  key={tp.key}
                  onClick={() => startQuiz(tp.key)}
                  className="bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 rounded-2xl p-4 text-center hover:scale-105 transition-transform"
                  data-testid={`ai-quiz-topic-${tp.key}`}
                >
                  <div className="text-4xl mb-1">{tp.emoji}</div>
                  <div className="font-bold text-gray-700">{lang === "it" ? tp.it : tp.en}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === "loading" && (
          <div className="bg-white rounded-3xl p-10 shadow-2xl text-center" data-testid="ai-quiz-loading">
            <div className="text-6xl mb-4 animate-bounce">✨</div>
            <h2 className="text-2xl font-bold text-purple-600 mb-2">{t.aiQuiz}</h2>
            <p className="text-gray-500">{t.aiGenerating}</p>
            <div className="flex justify-center gap-2 mt-6 text-3xl">
              <span className="animate-pulse">🤖</span>
              <span className="animate-pulse" style={{ animationDelay: "0.2s" }}>💭</span>
              <span className="animate-pulse" style={{ animationDelay: "0.4s" }}>❓</span>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="bg-white rounded-3xl p-10 shadow-2xl text-center" data-testid="ai-quiz-error">
            <div className="text-6xl mb-4">😅</div>
            <p className="text-gray-600 font-bold mb-6">{t.aiError}</p>
            <button
              onClick={() => startQuiz(topic)}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold hover:opacity-90"
              data-testid="ai-quiz-retry-btn"
            >
              🔄 {t.retry}
            </button>
          </div>
        )}

        {phase === "playing" && q && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl" data-testid="ai-quiz-question-card">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-bold text-purple-500">✨ {t.aiQuiz}</span>
              <span className="text-sm text-gray-500">{t.question} {current + 1} {t.of} {questions.length}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-6">
              <div
                className="bg-gradient-to-r from-violet-500 to-pink-500 h-2 rounded-full transition-all"
                style={{ width: `${((current + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
            <div className="text-5xl text-center mb-3">{q.emoji}</div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-800 text-center mb-6" data-testid="ai-quiz-question-text">{q.q}</h3>
            <div className="grid grid-cols-1 gap-3">
              {q.options.map((opt, idx) => {
                let cls = "bg-purple-50 hover:bg-purple-100 border-purple-200 text-gray-800";
                if (feedback) {
                  if (idx === feedback.correctIndex) cls = "bg-green-100 border-green-400 text-green-800";
                  else if (idx === feedback.selected) cls = "bg-red-100 border-red-400 text-red-700";
                  else cls = "bg-gray-50 border-gray-200 text-gray-400";
                }
                return (
                  <button
                    key={idx}
                    onClick={() => answer(idx)}
                    disabled={!!feedback}
                    className={`w-full px-4 py-3 rounded-xl border-2 font-bold text-left transition-colors ${cls}`}
                    data-testid={`ai-quiz-option-${idx}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            {feedback && (
              <p className={`text-center font-bold mt-4 ${feedback.isCorrect ? "text-green-600" : "text-red-500"}`} data-testid="ai-quiz-feedback">
                {feedback.isCorrect ? `🎉 ${t.correct}` : `❌ ${t.wrong}`}
              </p>
            )}
          </div>
        )}

        {phase === "results" && results && (
          <div className="bg-white rounded-3xl p-8 shadow-2xl text-center" data-testid="ai-quiz-results">
            <div className="text-6xl mb-4">{results.correct === results.total ? "🏆" : results.correct >= results.total / 2 ? "🌟" : "💪"}</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{t.gameOver}</h2>
            <div className="bg-purple-100 rounded-xl p-4 mb-4">
              <div className="text-gray-500">{t.score}</div>
              <div className="text-3xl font-bold text-purple-600">{results.correct}/{results.total}</div>
            </div>
            <div className="bg-yellow-100 rounded-xl p-4 mb-6">
              <div className="text-gray-500">{t.coinsEarned}</div>
              <div className="text-2xl font-bold text-yellow-600">+{results.coins_earned} 💰</div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onBack}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-purple-400 text-purple-600 font-bold hover:bg-purple-50"
                data-testid="ai-quiz-home-btn"
              >
                {t.backHome}
              </button>
              <button
                onClick={() => setPhase("topic")}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-pink-500 text-white font-bold hover:opacity-90"
                data-testid="ai-quiz-new-btn"
              >
                ✨ {t.newQuiz}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
